import PerfectScrollbar from "perfect-scrollbar";

export function stickyTableHeader() {
  const observer = new IntersectionObserver(
    (entries) => entries.forEach((e) => e.target.toggleAttribute("stuck", e.intersectionRatio < 1)),
    {
      threshold: [1],
    },
  );

  document.querySelectorAll(".table-wrapper th").forEach((th) => observer.observe(th));
}

const stickyTableColumnObservers = new WeakMap();
const tableScrollbarInstances = new WeakMap();
const tableScrollbarObservers = new WeakMap();

function syncTableScrollbarState(wrapper) {
  const table = wrapper.querySelector(":scope > table");
  const contentWidth = table instanceof HTMLElement ? table.scrollWidth : wrapper.scrollWidth;
  const contentHeight = table instanceof HTMLElement ? table.scrollHeight : wrapper.scrollHeight;
  const hasHorizontalOverflow = contentWidth > wrapper.clientWidth + 1;
  const hasVerticalOverflow = contentHeight > wrapper.clientHeight + 1;

  wrapper.classList.toggle("ps--active-x", hasHorizontalOverflow);
  wrapper.classList.toggle("ps--active-y", hasVerticalOverflow);
}

function queueTableScrollbarUpdate(wrapper) {
  const scrollbar = tableScrollbarInstances.get(wrapper);
  if (!scrollbar) {
    return;
  }

  window.requestAnimationFrame(() => {
    scrollbar.update();
    syncTableScrollbarState(wrapper);
  });
}

export function initTableScrollbars(root = document) {
  root.querySelectorAll(".table-wrapper:not(.no-scroll)").forEach((wrapper) => {
    if (!(wrapper instanceof HTMLElement)) {
      return;
    }

    const existingScrollbar = tableScrollbarInstances.get(wrapper);
    if (existingScrollbar) {
      queueTableScrollbarUpdate(wrapper);
      return;
    }

    const scrollbar = new PerfectScrollbar(wrapper, {
      wheelPropagation: true,
      suppressScrollX: false,
      suppressScrollY: false,
    });

    tableScrollbarInstances.set(wrapper, scrollbar);
    queueTableScrollbarUpdate(wrapper);

    if (tableScrollbarObservers.has(wrapper)) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => queueTableScrollbarUpdate(wrapper));
    resizeObserver.observe(wrapper);

    const table = wrapper.querySelector(":scope > table");
    if (table instanceof HTMLTableElement) {
      resizeObserver.observe(table);
    }

    tableScrollbarObservers.set(wrapper, resizeObserver);
  });
}

function setStickyColumnOffset(table, cellIndex, side, offset) {
  Array.from(table.rows).forEach((row) => {
    const cell = row.cells[cellIndex];
    if (!cell || !cell.classList.contains("sticky-column")) {
      return;
    }

    cell.classList.add("sticky-ready");

    if (side === "left") {
      cell.style.setProperty("--sticky-left", `${offset}px`);
      cell.style.removeProperty("--sticky-right");
      return;
    }

    cell.style.setProperty("--sticky-right", `${offset}px`);
    cell.style.removeProperty("--sticky-left");
  });
}

function getStickyCellWidth(cell) {
  return cell.getBoundingClientRect().width;
}

function resetStickyColumnOffsets(table) {
  table.querySelectorAll(".sticky-column").forEach((cell) => {
    cell.classList.remove("sticky-ready");
    cell.style.removeProperty("--sticky-left");
    cell.style.removeProperty("--sticky-right");
  });
}

function syncStickyTableColumns(table) {
  const headerRow = table.tHead?.rows?.[0];
  if (!headerRow) {
    return;
  }

  const headerCells = Array.from(headerRow.cells);
  const leftStickyCells = headerCells.filter(
    (cell) => cell.classList.contains("sticky-column") && !cell.classList.contains("on-right"),
  );
  const rightStickyCells = headerCells.filter(
    (cell) => cell.classList.contains("sticky-column") && cell.classList.contains("on-right"),
  );

  resetStickyColumnOffsets(table);

  let leftOffset = 0;
  leftStickyCells.forEach((cell) => {
    setStickyColumnOffset(table, cell.cellIndex, "left", leftOffset);
    leftOffset += getStickyCellWidth(cell);
  });

  let rightOffset = 0;
  [...rightStickyCells].reverse().forEach((cell) => {
    setStickyColumnOffset(table, cell.cellIndex, "right", rightOffset);
    rightOffset += getStickyCellWidth(cell);
  });
}

export function stickyTableColumns(root = document) {
  root.querySelectorAll(".table-wrapper > table").forEach((table) => {
    if (!(table instanceof HTMLTableElement)) {
      return;
    }

    const sync = () => {
      window.requestAnimationFrame(() => {
        syncStickyTableColumns(table);

        const wrapper = table.parentElement;
        if (wrapper instanceof HTMLElement) {
          queueTableScrollbarUpdate(wrapper);
        }
      });
    };

    sync();

    if (stickyTableColumnObservers.has(table)) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => sync());
    resizeObserver.observe(table);

    const headerRow = table.tHead?.rows?.[0];
    if (headerRow) {
      Array.from(headerRow.cells).forEach((cell) => resizeObserver.observe(cell));
    }

    stickyTableColumnObservers.set(table, resizeObserver);
  });
}
